import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Quote-type-aware wrapper for View Quote. Fetches the quote once,
 * then renders EstimatingViewAdditionalWorks or EstimatingViewSite
 * based on quoteType, passing the quote to avoid duplicate fetches.
 */
import { useState, useCallback, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { quotesService } from "@/api/services/quotes.service";
import { ADDITIONAL_WORKS_QUOTE_TYPE } from "@/utils/quoteHelpers";
import { EstimatingViewSite } from "./EstimatingViewSite";
import { EstimatingViewAdditionalWorks } from "./EstimatingViewAdditionalWorks";
import styles from "./EstimatingViewSite.module.css";
const BackIcon = () => (_jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: _jsx("path", { d: "M19 12H5M12 19l-7-7 7-7" }) }));
export const EstimatingViewQuote = () => {
    const { quoteId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const basePath = location.pathname.startsWith("/staff") ? "/staff" : "/admin";
    const fetchQuote = useCallback(async () => {
        if (!quoteId)
            return;
        try {
            setLoading(true);
            setError(null);
            const response = await quotesService.getQuote(quoteId);
            setQuote(response.data);
        }
        catch (err) {
            console.error("Error fetching quote:", err);
            setError("Failed to load quote. Please try again.");
        }
        finally {
            setLoading(false);
        }
    }, [quoteId]);
    useEffect(() => {
        fetchQuote();
    }, [fetchQuote]);
    const handleBack = () => {
        const previousPath = location.state?.from;
        if (previousPath)
            navigate(previousPath);
        else
            navigate(`${basePath}/estimating`);
    };
    if (loading) {
        return (_jsxs("div", { className: styles.page, children: [_jsxs("header", { className: styles.header, children: [_jsx("button", { onClick: handleBack, className: styles.backButton, children: _jsx(BackIcon, {}) }), _jsx("h1", { className: styles.headerTitle, children: "Loading Quote..." })] }), _jsx("div", { className: styles.reportContainer, children: _jsx("p", { children: "Loading quote details..." }) })] }));
    }
    if (error || !quote) {
        return (_jsxs("div", { className: styles.page, children: [_jsxs("header", { className: styles.header, children: [_jsx("button", { onClick: handleBack, className: styles.backButton, children: _jsx(BackIcon, {}) }), _jsx("h1", { className: styles.headerTitle, children: "Quote Not Found" })] }), _jsxs("div", { className: styles.reportContainer, children: [_jsx("p", { children: error || "Quote not found" }), _jsx("button", { onClick: handleBack, className: styles.retryBtn, children: "Back to Estimations" })] })] }));
    }
    if (quote.quoteType === ADDITIONAL_WORKS_QUOTE_TYPE) {
        return _jsx(EstimatingViewAdditionalWorks, { initialQuote: quote });
    }
    return _jsx(EstimatingViewSite, { initialQuote: quote });
};
